// ============================================================================
// QueuePingsPanel — Queue Pings inside the Referral Pings page (distinct UI).
//
// Recipient view (sector roles): received deliveries → Acknowledge + Open Queue.
// Sender view (ops_manager / tenant_admin): sent pings → ack summary + Dismiss.
//
// Queue Pings are acknowledge-only (no replies). Amber/orange "QUEUE PING" styling
// keeps them visually distinct from referral pings. NON-PHI, not referral-bound.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Megaphone, Loader2, AlertTriangle, Check, CheckCircle2, Ban, ArrowRight, Clock, Users,
} from 'lucide-react';
import {
  subscribeQueuePingDeliveriesForUser,
  subscribeQueuePingsForSender,
  acknowledgeQueuePingDelivery,
  listQueuePingDeliveries,
  dismissQueuePingCF,
  QUEUE_PING_PURPOSE_LABELS,
  QUEUE_PING_STATUS,
  QUEUE_PING_DELIVERY_STATUS,
  OPEN_QUEUE_ROUTE_MAP,
} from '../core/index.js';
import { ROLE_DISPLAY } from '../core/rbac.js';

const SECTOR_LABEL = {
  INTAKE: 'Intake Coordinators',
  TRIAGE: 'Triage Nurses',
  SCHEDULING: 'Schedulers',
  CLOSED_LOOP: 'Specialist Admins',
};

function fmtWhen(ts) {
  if (!ts) return '—';
  const ms = ts.toMillis ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : Date.parse(ts));
  if (!ms || Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function QueuePingBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-500/15 text-amber-300 border border-amber-500/30">
      <Megaphone className="w-3 h-3" /> Queue Ping
    </span>
  );
}

export default function QueuePingsPanel({ onNavigate }) {
  const { user } = useAuth();
  // Canonical actor id: the Firestore profile always carries `id` (= auth uid via
  // cleanDoc); `uid` is only present on some profiles. The rest of the app reads
  // `uid || id`, and Queue Ping deliveries are keyed by the recipient's auth uid,
  // so we must match here or the recipient query returns nothing.
  const uid = user?.uid || user?.id;
  const tenantId = user?.tenantId;
  const role = user?.role;
  const isSenderRole = role === 'ops_manager' || role === 'tenant_admin';

  const [rows, setRows] = useState([]);       // deliveries (recipient) or parent pings (sender)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [ackSummary, setAckSummary] = useState(null); // for sender: { total, acknowledged }
  const [queueTab, setQueueTab] = useState('new');    // recipient view: 'new' | 'ack'

  useEffect(() => {
    if (!tenantId || !uid) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    const onData = (data) => { setRows(data); setLoading(false); };
    const onError = (err) => {
      console.error('[QueuePingsPanel] listener error:', err?.code || err?.message);
      setError('Could not load queue pings.');
      setLoading(false);
    };
    const unsub = isSenderRole
      ? subscribeQueuePingsForSender(tenantId, uid, { onData, onError })
      : subscribeQueuePingDeliveriesForUser(tenantId, uid, { onData, onError });
    return () => unsub();
  }, [tenantId, uid, isSenderRole]);

  // Keep selection in sync with refreshed rows.
  useEffect(() => {
    if (!selected) return;
    const key = isSenderRole ? 'id' : 'queuePingId';
    const fresh = rows.find((r) => r[key] === selected[key]);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sender: load ack summary for the selected ping.
  const loadAckSummary = useCallback(async (queuePingId) => {
    setAckSummary(null);
    if (!tenantId || !queuePingId) return;
    try {
      const deliveries = await listQueuePingDeliveries(tenantId, queuePingId);
      const acknowledged = deliveries.filter((d) => d.status === QUEUE_PING_DELIVERY_STATUS.ACKNOWLEDGED).length;
      setAckSummary({ total: deliveries.length, acknowledged });
    } catch {
      setAckSummary(null);
    }
  }, [tenantId]);

  useEffect(() => {
    if (isSenderRole && selected?.id) loadAckSummary(selected.id);
  }, [isSenderRole, selected?.id, loadAckSummary]);

  const acknowledge = async (delivery) => {
    if (busy) return;
    setBusy(true); setActionError(null);
    try {
      await acknowledgeQueuePingDelivery(delivery.tenantId, delivery.queuePingId, uid);
      // The live subscription flips this delivery to ACKNOWLEDGED; follow it to the
      // Acknowledged tab so the selected detail stays visible (no stale selection).
      setQueueTab('ack');
    } catch (err) {
      setActionError('Could not acknowledge. Please retry.');
    } finally { setBusy(false); }
  };

  const dismiss = async (ping) => {
    if (busy) return;
    setBusy(true); setActionError(null);
    try {
      await dismissQueuePingCF(ping.id || ping.queuePingId);
    } catch (err) {
      setActionError('Could not dismiss queue ping.');
    } finally { setBusy(false); }
  };

  const openQueue = (sector) => {
    const target = OPEN_QUEUE_ROUTE_MAP[sector];
    if (target && onNavigate) onNavigate(target.route, target.navState);
  };

  // Recipient view: split active deliveries into New (unread) vs Acknowledged.
  // Dismissed parents (parentStatus !== OPEN) drop out of both active tabs.
  const newRows = rows.filter(
    (r) => r.status === QUEUE_PING_DELIVERY_STATUS.UNREAD && r.parentStatus === QUEUE_PING_STATUS.OPEN,
  );
  const ackRows = rows.filter(
    (r) => r.status === QUEUE_PING_DELIVERY_STATUS.ACKNOWLEDGED && r.parentStatus === QUEUE_PING_STATUS.OPEN,
  );
  const displayRows = isSenderRole ? rows : (queueTab === 'new' ? newRows : ackRows);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 py-10 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading queue pings…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
      </div>
    );
  }
  // Sender with nothing sent: simple empty state (no tabs).
  if (isSenderRole && rows.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-8 text-center text-sm text-slate-500">
        You have not sent any queue pings.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Recipient tabs: New (unread) vs Acknowledged. Sender view has no tabs. */}
      {!isSenderRole && (
        <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-800/60 w-fit">
          {[
            { id: 'new', label: 'New', count: newRows.length },
            { id: 'ack', label: 'Acknowledged', count: ackRows.length },
          ].map(({ id, label, count }) => (
            <button
              key={id}
              onClick={() => setQueueTab(id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                queueTab === id ? 'bg-primary-soft text-brand' : 'text-slate-400 hover:text-white hover:bg-slate-700/60'
              }`}
            >
              {label} <span className="text-xs opacity-70">{count}</span>
            </button>
          ))}
        </div>
      )}

      {!isSenderRole && displayRows.length === 0 ? (
        <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-8 text-center text-sm text-slate-500">
          {queueTab === 'new' ? 'No new queue pings.' : 'No acknowledged queue pings.'}
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* List */}
      <div className="lg:col-span-2 space-y-2">
        {displayRows.map((r) => {
          const isDelivery = !isSenderRole;
          const purpose = r.purpose;
          const sector = r.targetSector;
          const status = isDelivery ? r.parentStatus : r.status;
          const unread = isDelivery && r.status === QUEUE_PING_DELIVERY_STATUS.UNREAD && r.parentStatus === QUEUE_PING_STATUS.OPEN;
          const key = isDelivery ? r.queuePingId : r.id;
          const selectedKey = isSenderRole ? selected?.id : selected?.queuePingId;
          return (
            <button
              key={key}
              onClick={() => { setSelected(r); setActionError(null); }}
              className={`w-full text-left rounded-2xl border p-4 transition-all ${
                selectedKey === key
                  ? 'bg-amber-500/10 border-amber-500/40'
                  : unread
                    ? 'bg-slate-900/40 border-amber-500/25 hover:border-amber-500/40'
                    : 'bg-slate-900/40 border-slate-700/40 hover:border-slate-600/60'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  {unread && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />}
                  <QueuePingBadge />
                </span>
                {status === QUEUE_PING_STATUS.DISMISSED
                  ? <span className="text-[11px] text-slate-500">Dismissed</span>
                  : isDelivery && r.status === QUEUE_PING_DELIVERY_STATUS.ACKNOWLEDGED
                    ? <span className="text-[11px] text-emerald-400">Acknowledged</span>
                    : <span className="text-[11px] text-amber-300">{isDelivery ? 'New' : 'Open'}</span>}
              </div>
              <p className={`text-sm ${unread ? 'font-bold' : 'font-medium'} text-white`}>
                {QUEUE_PING_PURPOSE_LABELS[purpose] || purpose}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Target: {SECTOR_LABEL[sector] || sector}</p>
              <p className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />{fmtWhen(isDelivery ? r.parentCreatedAt : r.createdAt)}
              </p>
            </button>
          );
        })}
      </div>

      {/* Detail */}
      <div className="lg:col-span-3">
        {!selected ? (
          <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-10 text-center text-sm text-slate-500">
            Select a queue ping.
          </div>
        ) : (
          <div className="rounded-2xl border border-amber-500/20 bg-slate-900/40 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-slate-800/60">
              <QueuePingBadge />
              <h2 className="text-lg font-semibold text-white mt-2">{QUEUE_PING_PURPOSE_LABELS[selected.purpose] || selected.purpose}</h2>
              <p className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Target: {SECTOR_LABEL[selected.targetSector] || selected.targetSector}
              </p>
            </div>

            <div className="px-6 py-4 border-b border-slate-800/60 bg-slate-800/20">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Action needed</p>
              <p className="text-sm text-slate-200 leading-relaxed">{selected.actionNeeded}</p>
            </div>

            {selected.note && (
              <div className="px-6 py-3 border-b border-slate-800/60">
                <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Note</p>
                <p className="text-sm text-slate-300">{selected.note}</p>
              </div>
            )}

            <div className="px-6 py-3 text-xs text-slate-500">
              From {ROLE_DISPLAY[selected.senderRole]?.name ?? selected.senderRole}
              <span className="text-slate-700"> · </span>
              {fmtWhen(isSenderRole ? selected.createdAt : selected.parentCreatedAt)}
              {isSenderRole && ackSummary && (
                <span className="ml-2 text-amber-300">{ackSummary.acknowledged}/{ackSummary.total} acknowledged</span>
              )}
            </div>

            {actionError && (
              <div className="mx-6 mb-3 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{actionError}</p>
              </div>
            )}

            <div className="px-6 pb-5 flex flex-wrap gap-2">
              {!isSenderRole && (
                <>
                  <button
                    onClick={() => openQueue(selected.targetSector)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium transition-all"
                  >
                    <ArrowRight className="w-4 h-4" /> Open Queue
                  </button>
                  {selected.status === QUEUE_PING_DELIVERY_STATUS.UNREAD && selected.parentStatus === QUEUE_PING_STATUS.OPEN && (
                    <button
                      onClick={() => acknowledge(selected)}
                      disabled={busy}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium transition-all"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Acknowledge
                    </button>
                  )}
                  {selected.status === QUEUE_PING_DELIVERY_STATUS.ACKNOWLEDGED && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-emerald-400">
                      <CheckCircle2 className="w-4 h-4" /> Acknowledged
                    </span>
                  )}
                </>
              )}
              {isSenderRole && selected.status === QUEUE_PING_STATUS.OPEN && (
                <button
                  onClick={() => dismiss(selected)}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600 disabled:opacity-50 text-sm font-medium transition-all"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Dismiss
                </button>
              )}
              {isSenderRole && selected.status === QUEUE_PING_STATUS.DISMISSED && (
                <span className="text-sm text-slate-500">Dismissed · {fmtWhen(selected.dismissedAt)}</span>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
      )}
    </div>
  );
}
